import { Component, DestroyRef, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Location, ManageLocationDialogData } from 'src/app/models/location.model';
import { WorkingTime } from 'src/app/models/working_time.model';
import { ApiService } from 'src/app/services/api.service';
import { BisualSettingService } from 'src/app/services/bisual-setting.service';
import { UserService } from 'src/app/services/user.service';
import { ROUTER_COMPONENT_RELOAD_URL } from 'src/app/redirect-to-role-home';
import dayjs from 'dayjs';
import { CdkScrollable } from '@angular/cdk/scrolling';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
  MatExpansionPanelDescription,
} from '@angular/material/expansion';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { DatePipe } from '@angular/common';
import { SecondsToStringTimePipe } from 'src/app/pipes/seconds-to-string-time.pipe';
import { ManageLocationDialogComponent } from 'src/app/components/main/shared/locations/manage-location-dialog/manage-location-dialog.component';
import { LocationDialogModeEnum } from 'src/app/enums/location-dialog-mode.enum';
import { ProductTask } from 'src/app/models/product_task.model';
import { ProductTaskBoardTimeBudget } from 'src/app/models/product_task_board_time_budget.model';

interface WorkingTimeCustom extends WorkingTime {
  is_task_related: boolean;
  can_delete: boolean;
}

@Component({
  selector: 'app-working-time-history-day-detail-dialog',
  templateUrl: './working-time-history-day-detail-dialog.component.html',
  styleUrls: ['./working-time-history-day-detail-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription,
    MatButton,
    MatIconButton,
    MatIcon,
    MatTooltip,
    MatDialogActions,
    DatePipe,
    SecondsToStringTimePipe,
  ],
})
export class WorkingTimeHistoryDayDetailDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<WorkingTimeHistoryDayDetailDialogComponent>>(MatDialogRef);
  data = inject(MAT_DIALOG_DATA);
  private api = inject(ApiService);
  private user = inject(UserService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  private readonly destroy_ref = inject(DestroyRef);
  private readonly bisual_setting = inject(BisualSettingService);
  private readonly snack = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);

  worklogs: WorkingTimeCustom[];
  user_id: number;
  do_fetch: boolean = false;

  constructor() {
    this.worklogs = (this.data.worklogs as WorkingTime[]).map(worklog => ({
      ...worklog,
      is_task_related: worklog.relatable_type === 'App\\Models\\ProductTaskBoardTimeBudget',
      can_delete: false,
    }));
    this.dialogRef.beforeClosed().subscribe(() => {
      this.onNoClick();
    });
  }

  ngOnInit(): void {
    this.listenUserService();
  }

  onNoClick(): void {
    this.dialogRef.close(this.do_fetch);
  }

  getWorklogLocation(worklog: WorkingTimeCustom): Location | null {
    return worklog.location_ip?.location ?? worklog.location ?? null;
  }

  openLocationDialog(worklog: WorkingTimeCustom): void {
    let location: Location | null = this.getWorklogLocation(worklog);

    if (location === null) {
      return;
    }

    location = {
      ...(location as Location),
      location_ips: location.location_ips?.filter(ip => ip.id === worklog.location_ip?.id) ?? [],
    };

    this.dialog.open(ManageLocationDialogComponent, {
      data: { mode: LocationDialogModeEnum.View, location: location } satisfies ManageLocationDialogData,
    });
  }

  delete(i: number) {
    if (!this.worklogs[i].can_delete) {
      this.showWorkingTimeEditDeleteBlockedSnackBar();
      return;
    }

    if (confirm('¿Estás seguro que quieres borrar este registro de tiempo?')) {
      this.api.deleteWorkingTime(this.worklogs[i].id).subscribe(() => {
        this.worklogs.splice(i, 1);
      });
      this.do_fetch = true;
    }
  }

  goToTaskFromWorklog(worklog: WorkingTimeCustom): void {
    if (!worklog.is_task_related) {
      return;
    }

    if (worklog.relatable_type === 'App\\Models\\ProductTaskBoardTimeBudget') {
      const product_task_board_id = (worklog.relatable as ProductTaskBoardTimeBudget | undefined)?.product_task_board_id;

      if (product_task_board_id != null) {
        this.api
          .getProductTaskBoard(product_task_board_id, {
            with: 'product_task.company_product_phase,company_product_phase_board_col',
          })
          .pipe(takeUntilDestroyed(this.destroy_ref))
          .subscribe(board => {
            const company_product_phase_board_id = board.company_product_phase_board_col?.company_product_phase_board_id;
            this.navigateToTask(board.product_task, company_product_phase_board_id);
          });

        return;
      }
    }

    if (worklog.task_id != null) {
      this.api
        .getTask(worklog.task_id, {
          with: 'company_product_phase,product_task_boards.company_product_phase_board_col',
        })
        .pipe(takeUntilDestroyed(this.destroy_ref))
        .subscribe(task => this.navigateToTask(task));
    }
  }

  private navigateToTask(task: ProductTask, company_product_phase_board_id?: number): void {
    const has_board = company_product_phase_board_id != null || (task.product_task_boards?.length ?? 0) > 0;
    const targetRoute = has_board
      ? [
          '/employees',
          'development',
          'company-products',
          task.company_product_phase.company_product_id,
          'phases',
          task.company_product_phase_id,
          'boards',
          company_product_phase_board_id ?? task.product_task_boards[0].company_product_phase_board_col.company_product_phase_board_id,
        ]
      : [
          '/employees',
          'development',
          'company-products',
          task.company_product_phase.company_product_id,
          'phases',
          task.company_product_phase_id,
          'backlog',
        ];

    const mergedParams = { ...this.route.snapshot.queryParams, selectedIssue: task.code };

    this.router.navigateByUrl(ROUTER_COMPONENT_RELOAD_URL, { skipLocationChange: true }).then((): void => {
      this.router.navigate(targetRoute, { queryParams: mergedParams });
    });

    this.dialogRef.close();
  }

  private listenUserService() {
    this.user.currentUser.subscribe(data => {
      this.user_id = data.id;
      this.worklogs = this.worklogs.map(worklog => ({
        ...worklog,
        can_delete: this.user_id === worklog.user_id && this.canEditOrDeleteWorkingTime(worklog),
      }));
    });
  }

  private getWorkingTimeEditDeleteWindowHours(): number {
    return parseInt(this.bisual_setting.get('working-time-edit-delete-window-hours').value, 10);
  }

  private canEditOrDeleteWorkingTime(working_time: Pick<WorkingTime, 'created_at'>): boolean {
    const window_hours = this.getWorkingTimeEditDeleteWindowHours();

    return dayjs().isBefore(dayjs(working_time.created_at).add(window_hours, 'hour'));
  }

  private showWorkingTimeEditDeleteBlockedSnackBar(): void {
    const window_hours = this.getWorkingTimeEditDeleteWindowHours();

    this.snack.open(
      `No puedes editar o eliminar un registro de tiempo creado hace más de ${window_hours} hora${window_hours === 1 ? '' : 's'}.`,
      'Ok',
      { duration: 5000 }
    );
  }
}
